// Pixels framework — global bundle (ported from the Herd theme's main.js).
// Each component no-ops when its markup isn't on the page. initPage() runs
// once on load (page transitions removed).

// Import plugins JS
import Lenis from 'lenis'
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { SplitText } from "gsap/SplitText"

// Register the ScrollTrigger plugin with GSAP
gsap.registerPlugin(ScrollTrigger, SplitText);

// Import utility JS
import './utilities/helpers.js';
import { initContentRevealScroll, initMaskTextScrollReveal, revealStuckHeadings } from './utilities/animations.js';
import { initImageReveal } from './utilities/lazy-images.js';

// Import layout JS
import './layout/header.js';

// Import navigation
import { initNav } from './layout/nav.js';

// Import block JS
import './components/media.js';
import { initVideoFacades } from './components/video.js';
import { initMarqueeScrollDirection } from './components/marquee.js';
import { initLogoWallCycle } from './components/logo-wall.js';
import { initHeroGallery } from './components/hero-gallery.js';
import { initHeroShapes } from './components/hero-shapes.js';
import { initSocialIcons } from './layout/social-icons.js';
import { initTeamAnimations } from './components/team.js';
import { initStats } from './components/stats.js';
import { initSolutions } from './components/solutions.js';
import { initTotemPole } from './components/totem-pole.js';
import { initAccordion, initAccordionNav } from './components/accordion.js';
import { initToc } from './components/toc.js';
import { initArchiveFilter } from './components/archive-filter.js';
import { initTextCycle } from './components/text-cycle.js';
import { initTestimonials } from './components/testimonials.js';
import { initShare } from './components/share.js';

let lenis;

function initLenis() {
    if (lenis) {
        lenis.destroy();
    }
    lenis = new Lenis();
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => {
        lenis.raf(time * 1000);
    });
    gsap.ticker.lagSmoothing(0);

    // Smooth-scroll same-page anchor links.
    document.querySelectorAll('a[href^="#"]').forEach((link) => {
        const id = link.getAttribute('href');
        if (id.length <= 1) return;
        link.addEventListener('click', (e) => {
            const target = document.querySelector(id);
            if (!target) return;
            e.preventDefault();
            lenis.scrollTo(target, { offset: -80 });
        });
    });
}

function initPage() {
    ScrollTrigger.getAll().forEach(t => t.kill());
    gsap.globalTimeline.clear();

    // Revert any existing SplitText instances so fresh ones are created.
    // Don't strip data-split — the hero h1 declares it in markup, and
    // initMaskTextScrollReveal re-splits anything still carrying it.
    document.querySelectorAll('[data-split="heading"]').forEach(h => {
        if (h._splitInstance) {
            h._splitInstance.revert();
            h._splitInstance = null;
        }
    });

    initMaskTextScrollReveal();
    initMarqueeScrollDirection();
    initLogoWallCycle();
    initContentRevealScroll();
    initHeroShapes();
    initHeroGallery();
    initSocialIcons();
    initTeamAnimations();
    initStats();
    initSolutions();
    initTotemPole();
    initAccordion();
    initAccordionNav();
    initToc();
    initArchiveFilter();
    initTextCycle();
    initTestimonials();
    initShare();
    initVideoFacades();
    initNav();

    // After the galleries/carousels have cloned their slides, so the clones'
    // images get a reveal trigger too. Before the refresh below, so the new
    // triggers are included in it.
    initImageReveal();

    // Recalculate all scroll positions after the DOM has settled so triggers
    // for elements in the viewport correctly fire on this refresh.
    requestAnimationFrame(() => {
        ScrollTrigger.refresh();
    });
}

document.addEventListener('DOMContentLoaded', () => {
    initLenis();
    initPage();

    // The refresh inside initPage() runs on DOMContentLoaded — BEFORE images
    // have loaded and web fonts have settled, so ScrollTrigger computes trigger
    // positions against a layout where images are still 0-height. Once the real
    // images/fonts arrive and push content down, those positions are stale: an
    // above-the-fold reveal never fires and the element stays at autoAlpha 0
    // until a resize forces ScrollTrigger's own refresh. That is the "images
    // don't appear unless you resize the window" bug. Refreshing again once
    // everything is actually laid out fixes it.
    window.addEventListener('load', () => {
        ScrollTrigger.refresh();
        revealStuckHeadings();
    });

    // Fonts change text metrics (heading heights), which also shifts trigger
    // positions. Refresh when they're ready too. Guarded — not all browsers
    // expose document.fonts.
    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
            ScrollTrigger.refresh();
            revealStuckHeadings();
        });
    }

});
